/**
 * Отчёт по работам отдела ОА: период, тег, формат — и в очередь.
 *
 * В отчёт попадают задачи всех досок ОА, которые видит человек, с
 * выбранным тегом (или все — «без тега»): завершённые в периоде —
 * в «Выполнено», начатые и не завершённые к его концу — в «В работе». Файл собирает сервер в фоне,
 * как остальные выгрузки: о готовности приходит уведомление со ссылкой.
 */

import { FileSpreadsheet } from "lucide-react";
import { useState } from "react";

import { errorMessage, newRequestId } from "@taskflow/client-core";
import { previousMonth, todayIso } from "@/shared/lib/date";
import {
  Button,
  Checkbox,
  DateRangeInput,
  FormField,
  Modal,
  SegmentedControl,
  Select,
  toast,
} from "@/shared/ui";

import { useExportWorkReportMutation, useWorkReportTagsQuery } from "@taskflow/client-core/tasks";

type Format = "XLSX" | "PDF";

export function WorkReportDialog({ onClose }: { onClose: () => void }) {
  const initial = previousMonth(todayIso());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [tag, setTag] = useState<string | null>(null);
  const [withoutTag, setWithoutTag] = useState(false);
  const [format, setFormat] = useState<Format>("XLSX");
  const [error, setError] = useState<string | null>(null);
  const tags = useWorkReportTagsQuery();
  const [exportReport, { isLoading }] = useExportWorkReportMutation();

  const options = (tags.data?.data ?? []).map((item) => ({ id: item.name, label: item.name }));
  const invalidRange = from !== "" && to !== "" && from > to;

  const submit = async () => {
    if (from === "" || to === "") return setError("Укажите период: начало и окончание");
    if (invalidRange) return setError("Начало периода позже окончания");
    if (tag === null && !withoutTag) return setError("Выберите тег или отметьте «Без тега»");
    setError(null);
    try {
      const result = await exportReport({
        from,
        to,
        ...(withoutTag || tag === null ? { withoutTag: true } : { tag }),
        format,
        clientRequestId: newRequestId(),
      }).unwrap();
      toast.info(
        "Отчёт формируется",
        `Проектов: ${String(result.projects)}. Когда файл будет готов, появится уведомление со ссылкой`,
      );
      onClose();
    } catch (raw) {
      setError(errorMessage(raw, "Не удалось сформировать отчёт"));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Отчёт по работам"
      width="sm"
      dismissable={!isLoading}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button icon={FileSpreadsheet} loading={isLoading} onClick={() => void submit()}>
            Сформировать
          </Button>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p className="text-[13px] text-muted">
          Задачи всех досок ОА с выбранным тегом или все — «Без тега». «Выполнено» — завершённые за период,
          «В работе» — начатые и не завершённые к его концу.
        </p>
        <FormField label="Период" required>
          {(field) => (
            <DateRangeInput
              {...field}
              typeable
              from={from}
              to={to}
              invalid={invalidRange}
              clearable={false}
              onChange={(range) => {
                setFrom(range.from);
                setTo(range.to);
              }}
            />
          )}
        </FormField>
        <FormField
          label="Тег"
          required={!withoutTag}
          {...(tags.isSuccess && options.length === 0 ? { hint: "На досках ОА пока нет тегов" } : {})}
        >
          {(field) => (
            <Select
              id={field.id}
              label="Тег"
              placeholder={
                withoutTag ? "Все задачи, без тега" : tags.isLoading ? "Загрузка тегов…" : "Выберите тег"
              }
              options={options}
              value={withoutTag ? null : tag}
              disabled={tags.isLoading || withoutTag}
              onChange={setTag}
            />
          )}
        </FormField>
        <Checkbox
          label="Без тега — все задачи за период"
          checked={withoutTag}
          onChange={(checked) => {
            setWithoutTag(checked);
            setError(null);
          }}
        />
        <FormField label="Формат">
          {() => (
            <SegmentedControl<Format>
              label="Формат файла"
              value={format}
              onChange={setFormat}
              options={[
                { value: "XLSX", label: "Excel (.xlsx)" },
                { value: "PDF", label: "PDF (.pdf)" },
              ]}
            />
          )}
        </FormField>
        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
